import { useDialog } from "@datanac/datanac-mui-toolkit";
import { CircularProgress } from "@mui/material";
import { AppContext } from "App";
import EmptyCard from "components/Cards/EmptyCard";
import { propUpdateReducer } from "components/Core/propUpdateReducer";
import { useFarmController } from "components/Vault/Farm/useFarmController";
import WorkspaceHeader from "components/Workspaces/WorkspaceHeader";

import "leaflet/dist/leaflet.css";

import { useCallback, useContext, useEffect, useMemo, useReducer, useState } from "react";
import { useLocation } from "react-router";

import { getCurrentReinsuranceYear } from "@datanac/datanac-api-toolkit/dist/utility/InsurancePlanHelper";
import { useSnackbar } from "notistack";
import { GeoJSON, MapContainer, TileLayer } from "react-leaflet";

import { ReportsIcon } from "../../Icons/Icons";

export default function AcresMapReport() {
  const { globalState, globalDispatch } = useContext(AppContext);

  const location = useLocation();

  // ---

  const [filterLocationStateName, setFilterLocationStateName] = useState(null);
  const [filterLocationCountyName, setFilterLocationCountyName] = useState(null);

  //useEffect for location?.search - querystring changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location?.search);
    const location_state_name = searchParams.get("location_state_name");
    if (location_state_name?.length) {
      setFilterLocationStateName(location_state_name);
    } else {
      setFilterLocationStateName(null);
    }
  }, [location?.search]);
  useEffect(() => {
    const searchParams = new URLSearchParams(location?.search);
    const location_county_name = searchParams.get("location_county_name");
    if (location_county_name?.length) {
      setFilterLocationCountyName(location_county_name);
    } else {
      setFilterLocationCountyName(null);
    }
  }, [location?.search]);

  const { component: confirmDialogComponent, actions: confirmDialogActions } = useDialog();

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const handleError = (err) => {
    console.warn(err);
    enqueueSnackbar(err.detail || err.message || err, { preventDuplicate: true, variant: "error" });
  };

  const [currentYear, setCurrentYear] = useState(
    globalState?.crop_year ? globalState?.crop_year : getCurrentReinsuranceYear()
  );
  useEffect(() => {
    globalDispatch({
      type: "update",
      payload: {
        key: "crop_year",
        value: currentYear,
      },
    });
  }, [currentYear]);

  const [filter, dispatchFilter] = useReducer(propUpdateReducer, {
    commodity_name: [],
    practice_name: [],
  });

  const { counties, isLoadingCimsGeometry, cimsGeometry } = useFarmController({
    crop_year: currentYear,
    filter: filter,
    doLoadCIMS: true /* Load CIMS */,
    onError: handleError,
  });

  const onEachFeature = useCallback((feature, layer) => {
    // Add hover effect
    layer.on("mouseover", function () {
      this.setStyle({
        weight: 2,
        color: "colors.textBlue",
        fillColor: "colors.infoSkyBlue",
        fillOpacity: 0.5,
      });
      this.openPopup();
    });

    layer.on("mouseout", function () {
      this.setStyle({
        weight: 1,
        color: "colors.textBlue",
        fillColor: "colors.infoSkyBlue",
        fillOpacity: 0.3,
      });
      this.closePopup();
    });

    // Create a custom popup
    const popupContent = `
      <div style="
        padding: 8px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
        font-size: 14px;
        color: #2d3748;
        background: white;
        border-radius: 4px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        border: 1px solid #e2e8f0;
      ">
        <div style="font-weight: 600; margin-bottom: 4px; color: #2c5282;">Field Information</div>
        <div style="color: #4a5568;">${feature.properties.Name.replace(/&nbsp;/g, " ")}</div>
      </div>
    `;

    layer.bindPopup(popupContent, {
      maxWidth: 200,
      className: "custom-popup",
    });
  }, []);

  const mapGeoJsonData = useMemo(() => {
    if (!cimsGeometry?.length) return null;

    return {
      type: "FeatureCollection",
      crs: { type: "name", properties: { name: "urn:ogc:def:crs:OGC:1.3:CRS84" } },
      features: cimsGeometry?.map((geom) => ({
        type: "Feature",
        properties: {
          Name: `F&nbsp;${geom?.fsa_farm_number || ""} \n T&nbsp;${geom?.fsa_tract_number || ""}.${geom?.fsa_field_number || ""}`,
          altitudeMode: "clampToGround",
          tessellate: "-1",
          extrude: "0",
          visibility: "-1",
        },
        geometry: {
          type: "Polygon",
          coordinates: geom?.geometry?.coordinates,
        },
      })),
    };
  }, [cimsGeometry]);

  const [mapCenter, setMapCenter] = useState(null);
  useEffect(() => {
    if (cimsGeometry?.length) {
      const _mapCenter = cimsGeometry[0]?.geometry?.coordinates[0][0];
      const _mapCenterReversed = [..._mapCenter].reverse();
      setMapCenter(_mapCenterReversed);
    }
  }, [cimsGeometry]);

  // ---

  useEffect(() => {
    // Reducer counties[ii].ProducerFarmCrops
    var producerFarmCrops = [];
    counties
      ?.filter((county) => {
        const isCountyMatch =
          filterLocationCountyName == null || county?.location_county_name == filterLocationCountyName;
        const isStateMatch = filterLocationStateName == null || county?.location_state_name == filterLocationStateName;
        return isCountyMatch && isStateMatch;
      })
      ?.forEach((county) => {
        county?.ProducerFarms?.forEach((producerFarm) => {
          producerFarm?.ProducerFarmFieldCrops?.forEach((producerFarmFieldCrop) => {
            producerFarmCrops.push({
              ...producerFarmFieldCrop,
              CustomerName: producerFarm?.customer_name,
              OrderDate: producerFarm?.order_date,
              Freight: producerFarm?.freight,
              ShippedDate: producerFarm?.shipped_date,
            });
          });
        });
      });
  }, [counties, filterLocationStateName, filterLocationCountyName]);

  // ---

  return (
    <>
      <WorkspaceHeader
        title="Acres Map Report"
        category={{
          title: "Reports",
          icon: <ReportsIcon />,
        }}
      />

      {!isLoadingCimsGeometry && !cimsGeometry?.length && (
        <>
          <EmptyCard
            text="No results were found"
            secondaryText="We could not find any maps in the Vault.  Please ask your insurance agent to help get your information in the federal databases."
          />
        </>
      )}

      {isLoadingCimsGeometry && <CircularProgress />}

      {Boolean(mapCenter) && (
        <MapContainer
          style={{
            height: "80vh",
            width: "100%",
            borderRadius: "8px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
          zoom={12}
          center={mapCenter}
          zoomControl={true}
          preferCanvas={true}
          updateWhenZooming={false}
          updateWhenIdle={true}
          className="acres-map-report-page"
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            updateWhenZooming={false}
            updateWhenIdle={true}
          />
          {mapGeoJsonData && (
            <GeoJSON
              key="map01"
              data={mapGeoJsonData}
              onEachFeature={onEachFeature}
              updateWhenZooming={false}
              updateWhenIdle={true}
              style={{
                weight: 1,
                color: "colors.textBlue",
                fillColor: "colors.infoSkyBlue",
                fillOpacity: 0.3,
              }}
            />
          )}
        </MapContainer>
      )}

      {confirmDialogComponent}

      <style>
        {`
          .custom-popup .leaflet-popup-content-wrapper {
            background: transparent;
            box-shadow: none;
            padding: 0;
          }
          .custom-popup .leaflet-popup-tip {
            background: white;
          }
          .leaflet-container {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
          }
        `}
      </style>
    </>
  );
}
